/**
 * What the pointer does on the class diagram: drag the background to pan, the wheel to zoom,
 * click a box, an arrow, a bundle or a package to select it, Shift+drag from anywhere but a box to
 * select the boxes in a rectangle (Ctrl+Shift adds them), Ctrl+click a box to add it to the selection or
 * take it out, drag a box (all the selected ones when it is one of them, or a package and
 * everything in it, hidden boxes included) to move it, drag from a box's handle (or with the link tool, or Shift) to
 * another box to link them, Alt+click an arrow to delete it. In the blueprint, a box's "+" adds a
 * pin, and dragging a pin onto a box wires it there (onto empty space: a node to add, wired from
 * it); arrows are its wires. On a touch screen, two fingers pinch
 * to zoom and move the view (whatever the first finger had started is dropped). The middle
 * mouse button moves the view from anywhere, over a box, an arrow or a package too, and a
 * middle click selects nothing.
 *
 * Like the nodal view's `pointer.ts`, it keeps only the gesture in progress and reports through
 * callbacks; it works on screen points relative to the canvas.
 */
import { createSignal, type Accessor } from "solid-js";
import type { Link } from "@protocol/Link";
import type { Pin } from "@protocol/Pin";
import { blueprintHitAt, boxOfHit, hitAt, type BoxPositions, type Diagram, type DiagramBox, type DiagramFocus, type DiagramHit } from "../../../state/diagram";
import { handleOf } from "./paint";
import { sameEdge } from "../../../client/graph";
import { MIDDLE } from "../pointer";
import { DRAG_PX, EDGE_TOLERANCE_PX, HANDLE_RADIUS_PX, PIN_TOLERANCE_PX } from "../constants";

/** A point on the canvas, CSS pixels. */
export interface Point {
  x: number;
  y: number;
}

/** What the gestures need. */
export interface GestureDeps {
  diagram: Diagram;
  /** Whether boxes may be moved and links drawn (the user may edit). */
  canEdit: () => boolean;
  linkTool: () => boolean;
  /** The selected node's id (one node selected). */
  selectedNode: () => string | null;
  /** Every selected node (one or several). */
  selectedNodes: () => string[];
  selectNode(id: string | null): void;
  /** Selects several nodes (the box selection). */
  selectNodes(ids: string[]): void;
  selectLink(link: Link | null): void;
  selectGroup(id: string | null): void;
  selectBundle(key: string | null): void;
  /** What the user looks at (which bundles are open, so which arrows can be clicked). */
  focus: () => DiagramFocus;
  removeLink(link: Link): void;
  /** A link was drawn from one box to another, released at `at`. */
  linked(from: DiagramBox, to: DiagramBox, at: Point): void;
  /** A link was drawn from a box and released on empty space at `at` (a node to add there). */
  linkedToNothing(from: DiagramBox, at: Point): void;
  /** Whether the diagram is drawn as a blueprint (pins, "+", wires). */
  blueprint: () => boolean;
  /** A pin's wire was dropped on box `to`. */
  wired(from: DiagramBox, pin: Pin, to: DiagramBox): void;
  /** A pin's wire was dropped on empty space at `at` (a node to add there, wired from the pin). */
  wiredToNothing(from: DiagramBox, pin: Pin, at: Point): void;
  /** The "+" of a box was clicked: a new pin. */
  addPin(box: DiagramBox): void;
  setCursor(cursor: string): void;
  /** A press started (closes the link menu). */
  pressed(): void;
}

/** The gestures and what they show. */
export interface Gestures {
  hover: Accessor<DiagramHit>;
  /** The link being drawn, in world units (from a pin in the blueprint). */
  linking: Accessor<Linking | null>;
  /** The box selection's rectangle being drawn, in world units. */
  band: Accessor<Band | null>;
  /** What is at a point. */
  hitAt(p: Point): DiagramHit;
  down(e: PointerEvent, p: Point): void;
  move(e: PointerEvent, p: Point): void;
  up(e: PointerEvent, p: Point): void;
  leave(): void;
  wheel(e: WheelEvent, p: Point): void;
  /** Cancels the gesture in progress; `true` if there was one. */
  cancel(): boolean;
}

/** A rectangle drawn to select boxes, world units (`x1, y1` where the press was). */
export interface Band {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** A link being drawn: from a box (one of its pins: `pin`) to a point, world units. */
export interface Linking {
  from: DiagramBox;
  x: number;
  y: number;
  pin?: string;
}

/** The gesture in progress. */
type Press =
  | { type: "pan"; start: Point; last: Point; moved: boolean; middle?: boolean }
  | { type: "move"; start: Point; moved: boolean; hit: DiagramHit; boxes: { id: string; x: number; y: number }[] }
  | { type: "link"; from: DiagramBox; start: Point; pin?: Pin }
  | { type: "band"; adding: boolean };

/** Creates the gestures. */
export function createGestures(deps: GestureDeps): Gestures {
  const { diagram } = deps;
  const [hover, setHover] = createSignal<DiagramHit>({ type: "background" }, { equals: sameHit });
  const [linking, setLinking] = createSignal<Linking | null>(null);
  const [band, setBand] = createSignal<Band | null>(null);
  let press: Press | null = null;
  /** Fingers (or pointers) down on the canvas, by pointer id. */
  const pointers = new Map<number, Point>();
  /** A two-finger pinch in progress: the fingers' distance, zoom and middle when it started. */
  let pinch: { distance: number; k: number; mid: Point } | null = null;
  /** The two first fingers down. */
  const fingers = () => [...pointers.values()].slice(0, 2) as [Point, Point];

  const hit = (p: Point): DiagramHit => {
    const w = diagram.toWorld(p.x, p.y);
    if (deps.blueprint()) return blueprintHitAt(diagram.layout(), w.x, w.y, PIN_TOLERANCE_PX / diagram.view.k, deps.canEdit());
    return hitAt(diagram.layout(), w.x, w.y, EDGE_TOLERANCE_PX / diagram.view.k, deps.focus());
  };

  /** The box whose link handle is under `p`. */
  const handleAt = (p: Point): DiagramBox | null => {
    // The blueprint has pins instead.
    if (!deps.canEdit() || deps.blueprint()) return null;
    const layout = diagram.layout();
    const ids = [hover().type === "box" ? (hover() as { box: DiagramBox }).box.id : null, deps.selectedNode()];
    for (const id of ids) {
      const box = id ? layout.byId.get(id) : undefined;
      if (!box) continue;
      const h = handleOf(box, diagram.view.k);
      const w = diagram.toWorld(p.x, p.y);
      if (Math.hypot(w.x - h.x, w.y - h.y) * diagram.view.k <= HANDLE_RADIUS_PX + 3) return box;
    }
    return null;
  };

  /** The boxes a drag on `target` moves: the box (every selected one when it is one of them), or everything inside the package. */
  const movedBy = (target: DiagramHit): { id: string; x: number; y: number }[] => {
    const layout = diagram.layout();
    if (target.type === "box") {
      const selected = deps.selectedNodes();
      const ids = selected.includes(target.box.id) ? selected : [target.box.id];
      return ids.flatMap((id) => {
        const b = layout.byId.get(id);
        return b ? [{ id, x: b.x, y: b.y }] : [];
      });
    }
    if (target.type !== "package") return [];
    // Hidden boxes too: the package moves as a whole, its frame as it is.
    return (layout.contents.get(target.pack.id) ?? []).map((id) => ({ id, x: layout.positions[id]![0], y: layout.positions[id]![1] }));
  };

  /** The cursor for what is under the pointer. */
  const cursorFor = (h: DiagramHit, onHandle: boolean) => {
    if (h.type === "pin") return deps.canEdit() ? "crosshair" : "pointer";
    if (h.type === "plus") return "pointer";
    if (onHandle || (deps.linkTool() && h.type === "box")) return "crosshair";
    if (h.type === "box" || h.type === "package") return deps.canEdit() ? "move" : "pointer";
    if (h.type === "edge" || h.type === "bundle") return "pointer";
    return "grab";
  };

  return {
    hover,
    linking,
    band,
    hitAt: hit,
    down(e, p) {
      if (e.pointerType === "mouse" && e.button === MIDDLE) {
        // Not the browser's own use of the button (autoscroll, pasting on Linux).
        e.preventDefault();
        if (press || pinch) return;
        deps.pressed();
        pointers.set(e.pointerId, p);
        setHover({ type: "background" });
        press = { type: "pan", start: p, last: p, moved: false, middle: true };
        deps.setCursor("grabbing");
        return;
      }
      deps.pressed();
      if (e.button !== 0) return;
      pointers.set(e.pointerId, p);
      if (pointers.size >= 2) {
        // A second finger: a pinch. Whatever the first one started is dropped, unchanged.
        if (!pinch) {
          if (press?.type === "move" && press.moved) diagram.cancelMove();
          press = null;
          setLinking(null);
          setBand(null);
          const [a, b] = fingers();
          pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y) || 1, k: diagram.view.k, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
          diagram.redraw = true;
        }
        return;
      }
      const fromHandle = handleAt(p);
      const target = hit(p);
      if (target.type === "plus") {
        if (deps.canEdit()) deps.addPin(target.box);
        press = null;
        return;
      }
      if (target.type === "pin") {
        deps.selectNode(target.box.id);
        press = null;
        if (!deps.canEdit()) return;
        const w = diagram.toWorld(p.x, p.y);
        press = { type: "link", from: target.box, start: p, pin: target.pin };
        setLinking({ from: target.box, x: w.x, y: w.y, pin: target.pin.id });
        return;
      }
      if (fromHandle || (target.type === "box" && deps.canEdit() && (deps.linkTool() || e.shiftKey))) {
        const from = fromHandle ?? (target as { box: DiagramBox }).box;
        deps.selectNode(from.id);
        const w = diagram.toWorld(p.x, p.y);
        press = { type: "link", from, start: p };
        setLinking({ from, x: w.x, y: w.y });
        return;
      }
      if (target.type !== "box" && e.shiftKey) {
        const w = diagram.toWorld(p.x, p.y);
        press = { type: "band", adding: e.ctrlKey || e.metaKey };
        setBand({ x1: w.x, y1: w.y, x2: w.x, y2: w.y });
        deps.setCursor("crosshair");
        return;
      }
      if (target.type === "edge") {
        if (e.altKey && deps.canEdit()) deps.removeLink(target.edge.link);
        else deps.selectLink(target.edge.link);
        press = null;
        return;
      }
      if (target.type === "bundle") {
        deps.selectBundle(target.bundle.key);
        press = null;
        return;
      }
      if (target.type === "box" && (e.ctrlKey || e.metaKey)) {
        const selected = deps.selectedNodes();
        const id = target.box.id;
        deps.selectNodes(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
        press = null;
        return;
      }
      // A box already in the selection keeps it (a drag moves them all).
      if (target.type === "box" && !deps.selectedNodes().includes(target.box.id)) deps.selectNode(target.box.id);
      if (target.type === "package") deps.selectGroup(target.pack.id);
      if (target.type === "background") {
        press = { type: "pan", start: p, last: p, moved: false };
        deps.setCursor("grabbing");
        return;
      }
      press = { type: "move", start: p, moved: false, hit: target, boxes: deps.canEdit() ? movedBy(target) : [] };
    },
    move(e, p) {
      if (pointers.has(e.pointerId)) pointers.set(e.pointerId, p);
      if (pinch) {
        if (pointers.size < 2) return;
        const [a, b] = fingers();
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        diagram.pan(mid.x - pinch.mid.x, mid.y - pinch.mid.y);
        diagram.zoomAt(mid.x, mid.y, (pinch.k * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.distance)) / diagram.view.k);
        pinch.mid = mid;
        return;
      }
      if (!press) {
        const h = hit(p);
        setHover(h);
        deps.setCursor(cursorFor(h, !!handleAt(p)));
        return;
      }
      if (press.type === "pan") {
        if (!press.moved && Math.hypot(p.x - press.start.x, p.y - press.start.y) < DRAG_PX) return;
        press.moved = true;
        diagram.pan(p.x - press.last.x, p.y - press.last.y);
        press.last = p;
        return;
      }
      if (press.type === "band") {
        const w = diagram.toWorld(p.x, p.y);
        setBand((b) => b && { ...b, x2: w.x, y2: w.y });
        diagram.redraw = true;
        return;
      }
      if (press.type === "link") {
        const w = diagram.toWorld(p.x, p.y);
        setLinking({ from: press.from, x: w.x, y: w.y, pin: press.pin?.id });
        setHover(hit(p));
        diagram.redraw = true;
        return;
      }
      if (!press.boxes.length) return;
      if (!press.moved && Math.hypot(p.x - press.start.x, p.y - press.start.y) < DRAG_PX) return;
      press.moved = true;
      const dx = (p.x - press.start.x) / diagram.view.k;
      const dy = (p.y - press.start.y) / diagram.view.k;
      const positions: BoxPositions = {};
      for (const b of press.boxes) positions[b.id] = [Math.round(b.x + dx), Math.round(b.y + dy)];
      diagram.moveBoxes(positions);
    },
    up(e, p) {
      pointers.delete(e.pointerId);
      if (pinch) {
        // The pinch ends with its last finger; nothing else happens.
        if (pointers.size === 0) pinch = null;
        return;
      }
      const done = press;
      press = null;
      setLinking(null);
      const drawn = band();
      setBand(null);
      diagram.redraw = true;
      if (!done) return;
      if (done.type === "band") {
        deps.setCursor("grab");
        if (drawn) {
          const inside = boxesIn(diagram.layout().boxes, drawn);
          deps.selectNodes(done.adding ? [...deps.selectedNodes(), ...inside] : inside);
        }
        return;
      }
      if (done.type === "pan") {
        deps.setCursor("grab");
        if (!done.moved && !done.middle) deps.selectNode(null);
        return;
      }
      if (done.type === "link") {
        const to = boxOfHit(hit(p));
        const far = Math.hypot(p.x - done.start.x, p.y - done.start.y) > 16;
        if (to && to.id !== done.from.id) {
          if (done.pin) deps.wired(done.from, done.pin, to);
          else deps.linked(done.from, to, p);
        } else if (!to && far) {
          if (done.pin) deps.wiredToNothing(done.from, done.pin, p);
          else deps.linkedToNothing(done.from, p);
        }
        return;
      }
      if (done.moved) void diagram.endMove();
      // A click (no drag) on one of several selected boxes selects that box alone.
      else if (done.hit.type === "box" && deps.selectedNodes().length > 1) deps.selectNode(done.hit.box.id);
    },
    leave() {
      if (!press) setHover({ type: "background" });
      diagram.redraw = true;
    },
    wheel(e, p) {
      e.preventDefault();
      diagram.zoomAt(p.x, p.y, Math.exp(-e.deltaY * 0.0016));
    },
    cancel() {
      pointers.clear();
      pinch = null;
      const had = !!press;
      // Back where the boxes were: nothing was changed yet.
      if (press?.type === "move" && press.moved) diagram.cancelMove();
      press = null;
      setLinking(null);
      setBand(null);
      diagram.redraw = true;
      return had;
    },
  };
}

/** The ids of the boxes a rectangle touches. */
export function boxesIn(boxes: readonly DiagramBox[], band: Band): string[] {
  const x1 = Math.min(band.x1, band.x2);
  const x2 = Math.max(band.x1, band.x2);
  const y1 = Math.min(band.y1, band.y2);
  const y2 = Math.max(band.y1, band.y2);
  return boxes.filter((b) => b.x < x2 && b.x + b.w > x1 && b.y < y2 && b.y + b.h > y1).map((b) => b.id);
}

/** Whether two hits point at the same thing (so hovering the same box does not repaint). */
function sameHit(a: DiagramHit, b: DiagramHit): boolean {
  if (a.type !== b.type) return false;
  if (a.type === "box" || a.type === "plus") return a.box.id === (b as { box: DiagramBox }).box.id;
  if (a.type === "pin") return a.box.id === (b as typeof a).box.id && a.pin.id === (b as typeof a).pin.id;
  if (a.type === "package") return a.pack.id === (b as typeof a).pack.id;
  if (a.type === "bundle") return a.bundle.key === (b as typeof a).bundle.key;
  if (a.type === "edge") {
    const l = a.edge.link;
    const m = (b as typeof a).edge.link;
    return sameEdge(l, m);
  }
  return true;
}
